export const SKU_PREFIX = 'OEFY-';
export const SKU_PATTERN = /^OEFY-[A-Z0-9][A-Z0-9_-]{0,39}$/;
export const MAX_PRICE_CENTS = 100_000;
export const MAX_TRIAL_DAYS = 31;
export const TRIAL_CHECK_CENTS = 1;
export const TRIAL_REMINDER_DAYS = 2;

export function firstPaymentCents(order: {
  readonly amountCents: number;
  readonly trialDays: number | null;
}): number {
  return order.trialDays === null ? order.amountCents : TRIAL_CHECK_CENTS;
}

export function trialEnd(start: Date, trialDays: number): Date {
  return new Date(start.getTime() + trialDays * 86_400_000);
}

export function normalizeSku(value: string): string {
  return value.trim().toUpperCase();
}

export function isOefySku(value: string): boolean {
  return normalizeSku(value).startsWith(SKU_PREFIX);
}

export interface PricePoint {
  readonly startsAt: Date;
  /** The price test group this price is for; null or missing is the standard price. */
  readonly variant?: string | null;
}

export type PriceStage = 'past' | 'current' | 'scheduled';

export interface PriceTimelineEntry<T extends PricePoint> {
  readonly price: T;
  readonly stage: PriceStage;
  readonly endsAt: Date | null;
}

/** A price test group key: lower case letters, digits and dashes, like `onder-squla`. */
export const PRICE_VARIANT_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

function byStart<T extends PricePoint>(prices: readonly T[]): T[] {
  return [...prices].sort((first, second) => first.startsAt.getTime() - second.startsAt.getTime());
}

function latestStarted<T extends PricePoint>(prices: readonly T[], at: Date): T | null {
  const started = byStart(prices).filter((price) => price.startsAt.getTime() <= at.getTime());
  return started.at(-1) ?? null;
}

export function variantOf(price: PricePoint): string | null {
  return price.variant ?? null;
}

/** The prices of one group: the standard prices for null, else that variant's prices. */
export function pricesOfVariant<T extends PricePoint>(
  prices: readonly T[],
  variant: string | null,
): T[] {
  return prices.filter((price) => variantOf(price) === variant);
}

/**
 * The price that applies at a moment. With a variant, that variant's price in force wins;
 * without one (or when the variant has no price in force yet) the standard price applies.
 */
export function priceInForce<T extends PricePoint>(
  prices: readonly T[],
  at: Date,
  variant: string | null = null,
): T | null {
  const own = variant === null ? null : latestStarted(pricesOfVariant(prices, variant), at);
  return own ?? latestStarted(pricesOfVariant(prices, null), at);
}

export function priceForOrder<T extends PricePoint>(prices: readonly T[], paidAt: Date): T | null {
  return priceInForce(prices, paidAt) ?? byStart(pricesOfVariant(prices, null))[0] ?? null;
}

export function nextPrice<T extends PricePoint>(
  prices: readonly T[],
  at: Date,
  variant: string | null = null,
): T | null {
  return (
    byStart(pricesOfVariant(prices, variant)).find(
      (price) => price.startsAt.getTime() > at.getTime(),
    ) ?? null
  );
}

/** The timeline of one group of prices; pass the prices of a single variant. */
export function priceTimeline<T extends PricePoint>(
  prices: readonly T[],
  now: Date,
): PriceTimelineEntry<T>[] {
  const ordered = byStart(prices);
  const current = latestStarted(ordered, now);
  return ordered.map((price, index) => ({
    price,
    stage:
      price === current
        ? 'current'
        : price.startsAt.getTime() > now.getTime()
          ? 'scheduled'
          : 'past',
    endsAt: ordered[index + 1]?.startsAt ?? null,
  }));
}

/**
 * Where an analytics event comes from. Staging and production share one PostHog project, so
 * every event carries this to tell them apart.
 */
export const ANALYTICS_ENVIRONMENTS = ['development', 'staging', 'production'] as const;
export type AnalyticsEnvironment = (typeof ANALYTICS_ENVIRONMENTS)[number];

export interface PriceTestSettings {
  readonly enabled: boolean;
  readonly flagKey: string;
  readonly variants: readonly string[];
}

export const DEFAULT_PRICE_TEST: PriceTestSettings = {
  enabled: false,
  flagKey: 'prijstest-abonnement',
  variants: ['squla', 'gezond', 'onder-squla'],
};

/**
 * The price group for a visitor, from the feature flag value the experiment tool gave.
 * Only a configured variant of a running test counts; anything else means standard prices.
 */
export function chosenPriceVariant(settings: PriceTestSettings, flagValue: unknown): string | null {
  if (!settings.enabled || typeof flagValue !== 'string') {
    return null;
  }
  return settings.variants.includes(flagValue) ? flagValue : null;
}
